import { MaybeRefOrGetter, ref, toValue } from 'vue';
import api from '@/api';
import { Feature, FeedType, PageName } from '@/api/tracking';
import { MAX_SITE_EXCLUSIONS } from '@/constants';
import { isAxiosError } from 'axios';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import {
  AdpDocument,
  AdpDocumentOptionAction,
  AdpDocumentOptionActionName,
  isArticle,
  isPatent,
  isReport,
  KeywordFeed,
  PutExcludeSiteAndArticleError,
} from '@/types';
import { usePersonalFeedSiteExclusion } from '@/utils/actions/usePersonalFeedSiteExclusion';
import { getDocTypeDisplayName } from '@/utils/adpDocument';
import { useCopyDocumentLink } from '@/utils/composables/useCopyDocumentLink';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useLayoutVariant } from '@/utils/composables/useLayoutVariant';
import { useEmitter } from '@/utils/vue';

export type OptionMenuContext = {
  pageName: PageName;
  feature?: Feature;
  feedType?: MaybeRefOrGetter<FeedType | undefined>;
  themeId?: MaybeRefOrGetter<number | undefined>;
  themeExcludedSitesCount?: MaybeRefOrGetter<number | undefined>;
  rankInWholeFeed?: MaybeRefOrGetter<number | undefined>;
  enablePersonalFeedSiteExclusion?: boolean;
};

export type OptionMenuConfig = {
  undisplay?: boolean;
  siteExclusion?: boolean;
  notInterested?: boolean;
  excludeMedia?: boolean;
  removeGroupMark?: boolean;
};

export type OptionMenuHandlers = {
  onRemoveGroupMark?: (adpDocument: AdpDocument) => void;
};

const getArticleExclusionId = (adpDocument: AdpDocument): number | null =>
  'article_exclusion_id' in adpDocument
    ? (adpDocument.article_exclusion_id as number | null)
    : null;

const isNotInterestedEligible = (adpDocument: AdpDocument): boolean => {
  if (isReport(adpDocument)) return false;
  const isNonJpoPatent =
    isPatent(adpDocument) &&
    (adpDocument.lang !== 'ja' || adpDocument.publication_office !== 'jpo');
  return !isNonJpoPatent;
};

export const buildOptionActions = (
  adpDocument: AdpDocument,
  config: OptionMenuConfig = {},
): AdpDocumentOptionAction[] => {
  const actions: AdpDocumentOptionAction[] = [];
  const docTypeName = getDocTypeDisplayName(adpDocument);

  if (config.notInterested && isNotInterestedEligible(adpDocument)) {
    if (getArticleExclusionId(adpDocument) === null) {
      actions.push({
        label: `この${docTypeName}に興味がない`,
        icon: 'thumbs-down',
        emitName: 'not-interested',
      });
    } else {
      actions.push({
        label: `「この${docTypeName}に興味がない」を取り消し`,
        icon: 'thumbs-down',
        emitName: 'remove-not-interested',
      });
    }
  }

  if (config.excludeMedia) {
    actions.push({
      label: 'このメディアを除外',
      icon: 'ban',
      emitName: 'exclude-media',
    });
  }

  if (config.undisplay && isArticle(adpDocument)) {
    actions.push({
      label: '記事を非表示',
      subDescription:
        adpDocument.lang !== 'zh'
          ? 'AIが学習し、このような記事の配信が少なくなります'
          : undefined,
      icon: 'eye-alt',
      emitName: 'undisplay',
    });
  }

  if (config.siteExclusion && isArticle(adpDocument)) {
    actions.push({
      label: 'このメディアを除外',
      subDescription: '編集画面でも配信停止できます',
      icon: 'ban',
      emitName: 'site-exclusion',
    });
  }

  if (config.removeGroupMark) {
    actions.push({
      label: 'このグループからグループマークを外す',
      icon: 'user',
      emitName: 'remove-group-mark',
    });
  }

  return actions;
};

export const useAdpDocumentOptionMenu = (
  context: OptionMenuContext,
  handlers: OptionMenuHandlers = {},
) => {
  const { createSnackbar } = useSnackbar();
  const emitter = useEmitter();
  const { getCurrentPathForTracking } = useCurrentPathForTracking();
  const { copyLink } = useCopyDocumentLink();
  const layoutVariant = useLayoutVariant();

  const { excludeMediaFromPersonalNews } = usePersonalFeedSiteExclusion(
    ref(context.enablePersonalFeedSiteExclusion ?? false),
  );

  const trackingBaseData = () => ({
    pageName: context.pageName,
    pageUrl: getCurrentPathForTracking(),
    feature: context.feature,
    feedType: toValue(context.feedType),
    layoutVariant,
  });

  const undisplayArticle = async (
    adpDocument: AdpDocument,
    rankInWholeFeed?: number,
  ) => {
    if (!isArticle(adpDocument)) return;

    try {
      await api.excludeArticle(
        adpDocument.theme_id,
        adpDocument,
        trackingBaseData(),
        rankInWholeFeed,
      );
      createSnackbar({ message: '記事を非表示にしました', type: 'success' });
    } catch (err) {
      createSnackbar({
        message: '記事を非表示にできませんでした',
        type: 'error',
      });
      throw err;
    }

    emitter.emit('article-removed', {
      themeId: toValue(context.themeId),
      articleId: adpDocument.id,
      lang: adpDocument.lang,
      feedType: toValue(context.feedType),
      // FIXME: 無条件にupcastしてしまっているが、一旦修正保留
      feedDate: (adpDocument as KeywordFeed).feed_date,
    });
  };

  const siteExclusionArticle = async (
    adpDocument: AdpDocument,
    rankInWholeFeed?: number,
  ) => {
    if (!isArticle(adpDocument)) return;

    const themeExcludedSitesCount = toValue(context.themeExcludedSitesCount);
    if (
      themeExcludedSitesCount &&
      themeExcludedSitesCount >= MAX_SITE_EXCLUSIONS
    ) {
      createSnackbar({
        message: `上限（${MAX_SITE_EXCLUSIONS}件）に達しています。登録したメディアは設定から変更できます。`,
        type: 'error',
      });
      return;
    }

    try {
      await api.excludeSiteAndArticle(
        adpDocument,
        trackingBaseData(),
        rankInWholeFeed,
      );
      createSnackbar({
        message: 'メディアを除外しました（次回の更新から反映されます）',
        type: 'success',
      });
    } catch (err) {
      let errorMessage = 'メディアを除外できませんでした';
      if (
        isAxiosError<PutExcludeSiteAndArticleError>(err) &&
        err.response?.data.error === 'site limit exceed'
      ) {
        errorMessage = `上限（${MAX_SITE_EXCLUSIONS}件）に達しています。登録したメディアは設定から変更できます。`;
      } else if (
        isAxiosError<PutExcludeSiteAndArticleError>(err) &&
        err.response?.data.error === 'site already excluded'
      ) {
        errorMessage = '既に登録しているメディアです';
      }
      createSnackbar({ message: errorMessage, type: 'error' });
      return;
    }

    emitter.emit('article-removed', {
      themeId: toValue(context.themeId),
      articleId: adpDocument.id,
      lang: adpDocument.lang,
      feedType: toValue(context.feedType),
      // FIXME: 無条件にupcastしてしまっているが、一旦修正保留
      feedDate: (adpDocument as KeywordFeed).feed_date,
    });
  };

  const notInterestedArticle = async (
    adpDocument: AdpDocument,
    rankInWholeFeed?: number,
  ) => {
    const excludeResp = await api.excludeArticle(
      undefined,
      adpDocument,
      trackingBaseData(),
      rankInWholeFeed,
    );
    emitter.emit('article-updated', {
      ...adpDocument,
      article_exclusion_id: excludeResp.id,
    });
    try {
      createSnackbar({
        message: 'フィードバックをいただきありがとうございます',
        type: 'success',
        undoFunc: async () => {
          await api.deleteExcludeArticle(excludeResp.id);
          emitter.emit('article-updated', {
            ...adpDocument,
            article_exclusion_id: null,
          });
          createSnackbar({
            message: 'フィードバックを取り消しました',
            type: 'success',
          });
          emitter.emit('personal-feed-article-restored', adpDocument);
        },
      });
    } catch (err) {
      createSnackbar({
        message: 'フィードバックエラーになりました。',
        type: 'error',
      });
      throw err;
    }
    emitter.emit('personal-feed-article-removed', adpDocument);
  };

  const removeNotInterestedArticle = async (
    adpDocument: AdpDocument,
    rankInWholeFeed?: number,
  ) => {
    const articleExclusionId = getArticleExclusionId(adpDocument);
    if (!articleExclusionId) return;

    await api.deleteExcludeArticle(articleExclusionId);
    emitter.emit('article-updated', {
      ...adpDocument,
      article_exclusion_id: null,
    });
    try {
      createSnackbar({
        message: 'フィードバックをいただきありがとうございます',
        type: 'success',
        undoFunc: async () => {
          const excludeResp = await api.excludeArticle(
            undefined,
            adpDocument,
            trackingBaseData(),
            rankInWholeFeed,
          );
          emitter.emit('article-updated', {
            ...adpDocument,
            article_exclusion_id: excludeResp.id,
          });
          createSnackbar({
            message: 'フィードバックを取り消しました',
            type: 'success',
          });
        },
      });
    } catch (err) {
      createSnackbar({
        message: 'フィードバックエラーになりました。',
        type: 'error',
      });
      throw err;
    }
  };

  const handleOptionAction = (
    emitName: AdpDocumentOptionActionName,
    adpDocument: AdpDocument,
    rankInWholeFeed: number | undefined = toValue(context.rankInWholeFeed),
  ) => {
    switch (emitName) {
      case 'copy-link':
        return copyLink(adpDocument);
      case 'undisplay':
        return undisplayArticle(adpDocument, rankInWholeFeed);
      case 'site-exclusion':
        return siteExclusionArticle(adpDocument, rankInWholeFeed);
      case 'not-interested':
        return notInterestedArticle(adpDocument, rankInWholeFeed);
      case 'remove-not-interested':
        return removeNotInterestedArticle(adpDocument, rankInWholeFeed);
      case 'exclude-media':
        return excludeMediaFromPersonalNews(adpDocument);
      case 'remove-group-mark':
        return handlers.onRemoveGroupMark?.(adpDocument);
    }
  };

  return { buildOptionActions, handleOptionAction };
};
